// Design: Indexed blog post — table of contents + one reading column.
//
// HERO     Topic, headline, standfirst, byline, published/updated dates.
//          No cover image: it took half the hero to show a render that said
//          nothing about the article.
// LEFT     Sticky table of contents, and one quiet text link to the offer.
// COLUMN   720px (~63 characters) of prose in the reading face, with the key
//          takeaways, any diagram, ONE contextual CTA at the midpoint, the
//          author bio, a "read next" shelf, and one closing module.
//
// Conversion modules are capped at three per article and never two on screen
// at once — see lib/blogCta.ts for the reasoning and the topic mapping.

import { useState, useMemo, useEffect, useRef, Suspense } from "react";
import { keyTakeawayBoxFor } from "@/lib/blogSummary";
import { postExcerpt, stripTitleSuffix } from "@/lib/blogText";
import { wrapContentTables } from "@/lib/contentTables";
import { promoteStatSources } from "@/lib/contentStats";
import { DiagramWrapper } from "@/components/DiagramWrapper";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useParams } from "wouter";
import {
  getPostBySlug,
  getAuthorBySlug,
  getCategoryBySlug,
  getRelatedPosts,
  formatDate,
  AUTHOR_ANJAN_AVATAR,
  type BlogAuthor,
} from "@/lib/blogData";
import SiteLayout from "@/components/SiteLayout";
import { DIAGRAM_REGISTRY, diagramAnchor, hasDiagram } from "@/components/blog-diagrams/index";
import { findDiagramInsertPoint } from "@/lib/diagramPlacement";
import { ctaForCategories, LIST_POST_CTAS, type BlogCta } from "@/lib/blogCta";
import { CTA_SLOT, CTA_SLOT_2, insertCtaSlot, insertListCtaSlots } from "@/lib/blogCtaSlot";
import { matchCaseStudy, type CaseStudyMatch } from "@/lib/caseStudyMatch";
import NewsletterForm from "@/components/NewsletterForm";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";
import { SITE_URL } from "@/lib/brand";

/** Display face: headings, labels, UI. The brand voice. */
const ff = "'Barlow', 'Barlow Fallback', sans-serif";
/**
 * Reading face: article body. The same Barlow, at the size and leading the
 * blog shipped with.
 *
 * A serif (Literata) was tried here and rejected, then a larger Barlow was
 * tried and also rejected. This is back to the original: 17px on a 1.75 line
 * in #333. docs/brand.md is unambiguous anyway — "Barlow (Google Fonts). One
 * family, no exceptions."
 */
const reading = ff;
/**
 * The byline avatar follows the author, rather than being fixed to one person.
 *
 * It used to be AUTHOR_ANJAN_AVATAR at every call site, so a post bylined
 * "Indexed Research" still carried a photograph of Anjan — in the header, the
 * author card and the Person image in the structured data.
 */
const authorPhoto = (a?: BlogAuthor) => a?.avatar ?? AUTHOR_ANJAN_AVATAR;
const authorPhotoAbs = (a?: BlogAuthor) => `${SITE_URL}${authorPhoto(a)}`;

// ── Helpers ────────────────────────────────────────────────────────────────

function extractHeadings(html: string) {
  // Only H2s for TOC to keep it compact
  const matches = Array.from(html.matchAll(/<h2[^>]*>(.*?)<\/h2>/gi));
  return matches.map((m) => {
    const text = m[1].replace(/<[^>]+>/g, "");
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return { level: 2, id, text };
  });
}

function injectHeadingIds(html: string): string {
  return html.replace(/<h([23])([^>]*)>(.*?)<\/h[23]>/gi, (_match, level, attrs, inner) => {
    const text = inner.replace(/<[^>]+>/g, "");
    const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    return `<h${level}${attrs} id="${id}">${inner}</h${level}>`;
  });
}

// Convert single-cell blog-table elements (used as callout/quote boxes) to plain paragraphs
function stripSingleCellTables(html: string): string {
  // Match <table class="blog-table"><thead><tr><th>...</th></tr></thead><tbody></tbody></table>
  // or similar single-header-cell patterns with no data rows
  return html.replace(
    /<table[^>]*class="blog-table"[^>]*>\s*<thead>\s*<tr>\s*<th>([\s\S]*?)<\/th>\s*<\/tr>\s*<\/thead>\s*(?:<tbody>\s*<\/tbody>)?\s*<\/table>/gi,
    (_match, cellContent) => {
      // Trim whitespace, convert newlines to spaces
      const text = cellContent.replace(/\s+/g, " ").trim();
      return `<p>${text}</p>`;
    }
  );
}

// ── Extract FAQ pairs for FAQPage schema ───────────────────────────────────
function extractFAQPairs(html: string): { question: string; answer: string }[] {
  // Find the FAQ section: an H2 containing "FAQ" or "Frequently Asked Questions"
  const faqSectionMatch = html.match(/<h2[^>]*>.*?(?:FAQ|Frequently Asked Questions|Questions).*?<\/h2>([\s\S]*?)(?=<h2|$)/i);
  if (!faqSectionMatch) return [];

  const section = faqSectionMatch[1];
  const pairs: { question: string; answer: string }[] = [];

  // Extract H3 + following P pairs
  const h3Matches = Array.from(section.matchAll(/<h3[^>]*>(.*?)<\/h3>\s*(<p>[\s\S]*?<\/p>)/gi));
  for (const m of h3Matches) {
    const question = m[1].replace(/<[^>]+>/g, "").trim();
    const answer = m[2].replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
    if (question && answer) pairs.push({ question, answer });
  }
  return pairs;
}


/** Placeholder for the client-result block, split on like the CTA slot. */
const CASE_SLOT = '<div data-case-slot="1"></div>';

/**
 * Mark where the client-result block goes: just before the FAQ, else before
 * Related Reading, else at the end. Late in the piece on purpose — the
 * mid-article CTA sits at the midpoint, and proof lands best once the reader
 * has the argument.
 */
function insertCaseSlot(html: string): string {
  const at = [
    /<h2[^>]*>(?:(?!<\/h2>)[\s\S])*?\b(FAQ|Frequently Asked)/i,
    /<h2[^>]*>(?:(?!<\/h2>)[\s\S])*?Related Reading/i,
  ].map(re => html.search(re)).find(i => i >= 0);
  return at === undefined ? html + CASE_SLOT : html.slice(0, at) + CASE_SLOT + html.slice(at);
}

/**
 * A client result matched to the post (lib/caseStudyMatch.ts). Shows only the
 * case study's own headline and figures, and links to the full story.
 */
function CaseStudyProof({ match }: { match: CaseStudyMatch }) {
  const { study, metrics } = match;
  return (
    <aside className="case-proof" aria-label="Client result">
      <p className="case-proof-label">Client result · {study.industryLabel}</p>
      <p className="case-proof-heading">{study.cardHeadline}</p>
      <div className="case-proof-metrics">
        {metrics.map(m => (
          <div key={m.label} className="case-proof-metric">
            <span className="case-proof-value">{m.value}</span>
            <span className="case-proof-metric-label">{m.label}</span>
          </div>
        ))}
      </div>
      <a href={`/client-stories/${study.slug}`} className="case-proof-link">
        Read how we did it ({study.timeframe}) →
      </a>
    </aside>
  );
}

// ── The one mid-article module ─────────────────────────────────────────────

/** Normalize what someone types into a domain the tools can accept. */
function cleanDomain(value: string): string {
  return value.trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/.*$/, "").slice(0, 253);
}

/**
 * The mid-article module.
 *
 * For topics backed by one of our own tools it carries that tool's input, so a
 * reader who has just read why AI citation matters can check their own domain
 * from the column instead of being sent to a landing page. Everything else is
 * a plain link.
 */
function InlineCta({ cta }: { cta: BlogCta }) {
  const [domain, setDomain] = useState("");

  return (
    <aside className="inline-cta">
      <p className="inline-cta-label">{cta.label}</p>
      <h3 className="inline-cta-heading">{cta.heading}</h3>
      <p className="inline-cta-body">{cta.body}</p>

      {cta.tool ? (
        <form
          className="inline-cta-form"
          onSubmit={(e) => {
            e.preventDefault();
            const clean = cleanDomain(domain);
            if (!clean) return;
            window.location.href = `${cta.href}?domain=${encodeURIComponent(clean)}`;
          }}
        >
          <label htmlFor="inline-tool-domain" className="sr-only">Your domain</label>
          <input
            id="inline-tool-domain"
            type="text"
            inputMode="url"
            autoComplete="url"
            placeholder="yourdomain.com"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
          />
          <button type="submit" className="inline-cta-btn">
            {cta.action}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </form>
      ) : (
        <a href={cta.href} className="inline-cta-btn">
          {cta.action}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </a>
      )}
    </aside>
  );
}

// ── Table of Contents ──────────────────────────────────────────────────────

function TableOfContents({ headings, activeId }: {
  headings: { level: number; id: string; text: string }[];
  activeId: string;
}) {
  if (headings.length === 0) return null;

  return (
    <div>
      <p style={{ color: "#111", fontSize: "12px", fontWeight: 800, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 14px" }}>
        Table of Contents
      </p>
      <nav>
        {headings.map((h, i) => (
          <a
            key={i}
            href={`#${h.id}`}
            style={{
              display: "block",
              padding: "5px 0",
              color: activeId === h.id ? "#157f8b" : "#333",
              fontSize: "14px",
              fontFamily: ff,
              fontWeight: activeId === h.id ? 700 : 500,
              textDecoration: "none",
              lineHeight: 1.45,
              transition: "color 0.15s",
            }}
            onMouseEnter={(e) => {
              if (activeId !== h.id) (e.currentTarget as HTMLAnchorElement).style.color = "#157f8b";
            }}
            onMouseLeave={(e) => {
              if (activeId !== h.id) (e.currentTarget as HTMLAnchorElement).style.color = "#333";
            }}
            onClick={(e) => {
              e.preventDefault();
              const el = document.getElementById(h.id);
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
          >
            {h.text}
          </a>
        ))}
      </nav>
    </div>
  );
}

// ── Share buttons ──────────────────────────────────────────────────────────

function ShareBtn({ href, label, children }: { href: string; label: string; children: React.ReactNode }) {
  const [hov, setHov] = useState(false);
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: "34px", height: "34px", borderRadius: "50%",
        border: `1px solid ${hov ? "#57d7e2" : "rgba(0,0,0,0.15)"}`,
        color: hov ? "#157f8b" : "rgba(0,0,0,0.45)",
        textDecoration: "none",
        transition: "border-color 0.15s, color 0.15s",
        flexShrink: 0,
      }}
    >
      {children}
    </a>
  );
}

/** Copy-link share control — the share action people actually reach for. */
function CopyLinkBtn({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const [hov, setHov] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1800);
    return () => clearTimeout(t);
  }, [copied]);
  return (
    <button
      type="button"
      aria-label={copied ? "Link copied" : "Copy link to this article"}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      onClick={() => {
        navigator.clipboard?.writeText(url).then(
          () => setCopied(true),
          () => {},
        );
      }}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: "34px", height: "34px", borderRadius: "50%",
        border: `1px solid ${copied ? "#157f8b" : hov ? "#57d7e2" : "rgba(0,0,0,0.15)"}`,
        color: copied || hov ? "#157f8b" : "rgba(0,0,0,0.55)",
        background: "transparent", cursor: "pointer", padding: 0,
        transition: "border-color 0.15s, color 0.15s",
        flexShrink: 0,
      }}
      title={copied ? "Copied" : "Copy link"}
    >
      {copied ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </button>
  );
}

// ── Article renderer with optional diagram injection ──────────────────────

/**
 * Render the article body, interleaving the two React modules that live inside
 * it: the mid-article CTA (at the slot marker) and the interactive diagram (at
 * the densest plain-text section break).
 *
 * The body is a single HTML string, so each module is placed by splitting the
 * string at its marker. Anything that cannot be located is simply not
 * rendered — an article missing a marker still renders in full.
 */
function ArticleBody({ slug, html, cta, cta2, caseStudy }: { slug: string; html: string; cta: BlogCta; cta2?: BlogCta; caseStudy: CaseStudyMatch | null }) {
  const Diagram = hasDiagram(slug) ? DIAGRAM_REGISTRY[slug] : undefined;

  // Diagram marker: the H2 the placement helper picked.
  const insertId = Diagram ? findDiagramInsertPoint(html) : null;
  const diagramMarker = insertId ? `<h2 id="${insertId}"` : null;
  const diagramAt = diagramMarker ? html.indexOf(diagramMarker) : -1;
  const ctaAt = html.indexOf(CTA_SLOT);
  const cta2At = html.indexOf(CTA_SLOT_2);
  const caseAt = html.indexOf(CASE_SLOT);

  // Cut points in document order, so the segments come out in the right order
  // whichever module happens to sit first.
  const cuts: { at: number; skip: number; node: React.ReactNode }[] = [];
  if (ctaAt !== -1) {
    cuts.push({ at: ctaAt, skip: CTA_SLOT.length, node: <InlineCta key="cta" cta={cta} /> });
  }
  if (cta2At !== -1) {
    cuts.push({ at: cta2At, skip: CTA_SLOT_2.length, node: cta2 ? <InlineCta key="cta2" cta={cta2} /> : null });
  }
  if (caseAt !== -1) {
    // The slot is always removed; the block renders only when a story matched.
    cuts.push({ at: caseAt, skip: CASE_SLOT.length, node: caseStudy ? <CaseStudyProof key="case" match={caseStudy} /> : null });
  }
  if (Diagram && diagramAt !== -1) {
    cuts.push({
      at: diagramAt,
      skip: 0,
      node: (
        <DiagramWrapper key="diagram" slug={slug}>
          <Suspense fallback={null}>
            <Diagram />
          </Suspense>
        </DiagramWrapper>
      ),
    });
  }
  cuts.sort((a, b) => a.at - b.at);

  if (cuts.length === 0) {
    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  }

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  cuts.forEach((cut, i) => {
    const segment = html.slice(cursor, cut.at);
    if (segment) parts.push(<div key={`html-${i}`} dangerouslySetInnerHTML={{ __html: segment }} />);
    parts.push(cut.node);
    cursor = cut.at + cut.skip;
  });
  const tail = html.slice(cursor);
  if (tail) parts.push(<div key="html-tail" dangerouslySetInnerHTML={{ __html: tail }} />);

  return <>{parts}</>;
}

// ── Main component ─────────────────────────────────────────────────────────

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [activeId, setActiveId] = useState("");
  const [tocOpen, setTocOpen] = useState(false);
  // Scroll progress and the back-to-top button are written straight to the DOM
  // through these refs. Holding them in React state re-rendered the whole
  // article on every scroll tick, which re-ran the content pipeline below.
  const progressRef = useRef<HTMLDivElement>(null);
  const backTopRef = useRef<HTMLButtonElement>(null);
  const isMobile = useIsMobile();

  // Every hook runs before the not-found branch returns. They used to sit
  // below it, so rendering a missing slug changed the hook order.
  const post = getPostBySlug(slug ?? "");
  const headings = useMemo(() => (post ? extractHeadings(post.content) : []), [post]);
  const author = post ? getAuthorBySlug(post.author) : undefined;
  const related = useMemo(() => (post ? getRelatedPosts(post, 4) : []), [post]);
  const primaryCat = post?.categories[0] ? getCategoryBySlug(post.categories[0]) : null;
  const faqPairs = useMemo(() => (post ? extractFAQPairs(post.content) : []), [post]);

  // The mid-article offer, matched to what the reader is reading about.
  const listCtas = post ? LIST_POST_CTAS[post.slug] : undefined;
  const inlineCta = useMemo(() => listCtas?.[0] ?? ctaForCategories(post?.categories ?? []), [post, listCtas]);

  // The client story closest to what the post is about, if any.
  const caseStudy = useMemo(() => (post ? matchCaseStudy(post) : null), [post]);

  // Four regex passes over the full article plus CTA insertion. Memoized on
  // the post so it runs once per article rather than once per render.
  const processedContent = useMemo(
    () =>
      post
        ? wrapContentTables(
            insertCaseSlot((listCtas ? insertListCtaSlots : insertCtaSlot)(promoteStatSources(injectHeadingIds(stripSingleCellTables(post.content))))),
          )
        : "",
    [post, listCtas],
  );
  const keyTakeawayBox = useMemo(() => (post ? keyTakeawayBoxFor(post) : ""), [post]);

  const headline = post ? stripTitleSuffix(post.title) : "";
  const standfirst = post ? postExcerpt(post) : "";

  // Progress bar + back-to-top. Where the browser supports scroll-driven
  // animations the progress bar is pure CSS and this listener only runs for
  // the button; elsewhere it drives both. Either way it never sets state.
  useEffect(() => {
    if (!post) return;
    const cssProgress =
      typeof CSS !== "undefined" && CSS.supports?.("animation-timeline", "scroll()");
    let frame = 0;
    const paint = () => {
      frame = 0;
      const docEl = document.documentElement;
      const scrolled = docEl.scrollTop || document.body.scrollTop;
      if (!cssProgress && progressRef.current) {
        const total = docEl.scrollHeight - docEl.clientHeight;
        const pct = total > 0 ? Math.min(1, scrolled / total) : 0;
        progressRef.current.style.transform = `scaleX(${pct})`;
      }
      if (backTopRef.current) {
        const show = scrolled > 500;
        backTopRef.current.style.opacity = show ? "1" : "0";
        backTopRef.current.style.pointerEvents = show ? "auto" : "none";
        backTopRef.current.style.transform = show ? "scale(1)" : "scale(0.8)";
      }
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(paint);
    };
    paint();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [post]);

  // Active TOC entry. An observer fires only when a heading crosses the band
  // near the top of the viewport, instead of measuring every heading on every
  // scroll tick.
  useEffect(() => {
    if (headings.length === 0) return;
    const visible = new Set<string>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }
        // The topmost heading currently in the band wins; when the band is
        // empty (mid-section) the previous choice stands.
        const first = headings.find((h) => visible.has(h.id));
        if (first) setActiveId(first.id);
      },
      { rootMargin: "-88px 0px -70% 0px", threshold: 0 },
    );
    for (const h of headings) {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [headings]);

  // Never cut the headline: a title ending "Plans, Costs and…" in search
  // results loses the click. The brand suffix goes only where it fits; Google
  // shortens a long title at a word break on its own. Matches
  // scripts/inject-canonicals.mjs, which writes the same tag into static HTML.
  const titleTag = post ? (headline.length <= 50 ? `${headline} | Indexed` : headline) : "Article not found | Indexed";
  const descTag = post
    ? (post.metaDescription || standfirst || `Read ${headline} on the Indexed blog.`).substring(0, 155)
    : "This article may have been moved or removed. Browse the latest posts on the Indexed blog.";
  // Always the live address: prerendering runs on localhost:4173, and the page
  // hydrates in place, so window.location would bake that host into the share
  // links of every built post (and pass along any ?utm query on the client).
  const pageUrl = `${SITE_URL}/blog/${post?.slug ?? ""}`;

  useSeoMeta({
    title: titleTag,
    description: descTag,
    canonical: post ? `/blog/${post.slug}` : undefined,
    noIndex: !post,
    ogType: "article",
    // The post's own cover, as before. A generated share card was tried and
    // reverted along with the generated covers on cards.
    ogImage: post?.coverImage ? `${SITE_URL}${post.coverImage}` : undefined,
    jsonLd: post
      ? [
          {
            "@type": "BlogPosting",
            "headline": headline,
            "description": descTag,
            "image": post.coverImage ? `${SITE_URL}${post.coverImage}` : undefined,
            "datePublished": post.publishedAt,
            "dateModified": post.updatedAt || post.publishedAt,
            // A team byline is an Organization, not a Person. Claiming a
            // named human wrote something the research team produced is the
            // kind of thing this blog's own articles warn about, and the
            // reviewer is named separately so accountability is still there.
            "author": author
              ? author.kind === "organization"
                ? {
                    "@type": "Organization",
                    "name": author.name,
                    "url": `https://www.joinindexed.com/author/${author.slug}`,
                  }
                : {
                    "@type": "Person",
                    "name": author.name,
                    "url": `https://www.joinindexed.com/author/${author.slug}`,
                    "image": authorPhotoAbs(author),
                    "jobTitle": author.role,
                  }
              : { "@type": "Organization", "name": "Indexed" },
            ...(author?.reviewedBy
              ? { "reviewedBy": { "@type": "Person", "name": author.reviewedBy } }
              : {}),
            "publisher": {
              "@type": "Organization",
              "name": "Indexed",
              "url": "https://www.joinindexed.com",
              "logo": { "@type": "ImageObject", "url": "https://www.joinindexed.com/og-default.jpg" },
            },
            "url": `https://www.joinindexed.com/blog/${post.slug}`,
            "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.joinindexed.com/blog/${post.slug}` },
            "keywords": post.categories.join(", "),
            "wordCount": Math.round((post.content?.length ?? 0) / 5),
            "timeRequired": `PT${post.readingTime}M`,
            "inLanguage": "en-GB",
            "isPartOf": { "@type": "Blog", "@id": "https://www.joinindexed.com/blog", "name": "Indexed Blog" },
          },
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.joinindexed.com" },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://www.joinindexed.com/blog" },
              { "@type": "ListItem", "position": 3, "name": headline, "item": `https://www.joinindexed.com/blog/${post.slug}` },
            ],
          },
          ...(faqPairs.length > 0 ? [{
            "@type": "FAQPage",
            "mainEntity": faqPairs.map(({ question, answer }) => ({
              "@type": "Question",
              "name": question,
              "acceptedAnswer": { "@type": "Answer", "text": answer },
            })),
          }] : []),
          ...(post.video ? [{
            "@type": "VideoObject",
            "name": post.video.title,
            "description": descTag,
            "thumbnailUrl": `https://i.ytimg.com/vi/${post.video.youtubeId}/maxresdefault.jpg`,
            "uploadDate": post.video.uploadDate,
            "duration": post.video.duration,
            "embedUrl": `https://www.youtube-nocookie.com/embed/${post.video.youtubeId}`,
            "contentUrl": `https://www.youtube.com/watch?v=${post.video.youtubeId}`,
          }] : []),
        ]
      : undefined,
  });

  if (!post) {
    return (
      <SiteLayout>
        <div style={{ background: "#0c0c0c", minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "120px 24px 80px" }}>
          <div style={{ textAlign: "center", maxWidth: "480px" }}>
            <h1 style={{ color: "white", fontSize: "clamp(28px, 4vw, 36px)", fontWeight: 700, fontFamily: ff, marginBottom: "12px" }}>Post not found</h1>
            <p style={{ color: "rgba(255,255,255,0.62)", fontSize: "16px", fontFamily: ff, lineHeight: 1.7, marginBottom: "32px" }}>This article may have been moved or removed. Browse our latest posts below.</p>
            <a href="/blog" style={{ fontFamily: ff, fontSize: "15px", fontWeight: 600, color: "#0c0c0c", background: "#57d7e2", padding: "12px 28px", borderRadius: "6px", textDecoration: "none", display: "inline-block" }}>Browse the Blog</a>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div style={{ background: "#fff", minHeight: "100vh" }}>

        {/* Reading progress — a full-width bar scaled on the X axis, so the
            browser animates a transform rather than relaying out a width. */}
        <div
          ref={progressRef}
          className="reading-progress"
          aria-hidden="true"
          style={{
            position: "fixed", top: 0, left: 0, zIndex: 9999,
            height: "3px", width: "100%",
            transform: "scaleX(0)", transformOrigin: "0 50%",
            background: "linear-gradient(90deg, #57d7e2, #3bb8c4)",
            pointerEvents: "none",
          }}
        />

        {/* Back to top — always mounted, faded in past 500px by the scroll
            handler, so showing it never re-renders the article. */}
        <button type="button"
          ref={backTopRef}
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          style={{
            position: "fixed", bottom: "32px", right: "32px", zIndex: 999,
            width: "44px", height: "44px", background: "#57d7e2",
            border: "none", borderRadius: "50%", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 16px rgba(87,215,226,0.35)",
            opacity: 0, pointerEvents: "none", transform: "scale(0.8)",
            transition: "opacity 0.18s ease, transform 0.18s ease",
          }}
          title="Back to top"
          aria-label="Back to top"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path d="M8 12V4M4 8l4-4 4 4" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {/* ── HERO ── */}
        <div style={{ background: "#0a0a0a", marginTop: "72px" }}>
          <div style={{ maxWidth: "1240px", margin: "0 auto", padding: isMobile ? "40px 20px 36px" : "56px 48px 52px" }}>

            {/* Breadcrumb — 44px tall so it is a real touch target on a phone */}
            <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "24px" }}>
              <a href="/blog" style={{ color: "rgba(255,255,255,0.62)", fontSize: "12px", fontFamily: ff, textDecoration: "none", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", transition: "color 0.15s", display: "inline-flex", alignItems: "center", minHeight: "44px" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#57d7e2")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.62)")}>Blog</a>
              {primaryCat && (
                <>
                  <span aria-hidden="true" style={{ color: "rgba(255,255,255,0.35)", fontSize: "12px" }}>/</span>
                  <a href={`/blog/category/${primaryCat.slug}`} style={{ color: "rgba(255,255,255,0.62)", fontSize: "12px", fontFamily: ff, textDecoration: "none", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", transition: "color 0.15s", display: "inline-flex", alignItems: "center", minHeight: "44px" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#57d7e2")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.62)")}>{primaryCat.label}</a>
                </>
              )}
            </nav>

            {/* Hero: title, standfirst and byline on the left, the post's
                cover on the right — as it was. */}
            <div style={{
              display: "grid",
              gridTemplateColumns: isMobile || !post.coverImage ? "1fr" : "1fr 1fr",
              gap: isMobile ? "28px" : "72px",
              alignItems: "center",
              maxWidth: post.coverImage && !isMobile ? undefined : "68ch",
            }}>
              <div>
                <h1 style={{ color: "#ffffff", fontFamily: ff, fontWeight: 800, fontSize: "clamp(30px, 3.4vw, 50px)", lineHeight: 1.06, letterSpacing: "-0.03em", margin: "0 0 20px", textWrap: "balance" }}>
                  {headline}
                </h1>
                {/* Standfirst — why read this. Falls back to the meta
                    description, which the pipeline always writes, because
                    `excerpt` is empty on almost every post. */}
                {standfirst && (
                  <p style={{ color: "rgba(255,255,255,0.72)", fontFamily: ff, fontSize: isMobile ? "17px" : "19px", lineHeight: 1.55, margin: "0 0 28px", maxWidth: "60ch" }}>
                    {standfirst}
                  </p>
                )}
                {author && (
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <a href={`/author/${author.slug}`} aria-label={`View ${author.name}'s author page`} style={{ flexShrink: 0, display: "block" }}>
                      <img src={authorPhoto(author)} alt="" style={{ width: "40px", height: "40px", borderRadius: "50%", objectFit: "cover", display: "block" }} />
                    </a>
                    <div>
                      <a href={`/author/${author.slug}`} style={{ textDecoration: "none", color: "rgba(255,255,255,0.92)", fontFamily: ff, fontSize: "14px", fontWeight: 600, transition: "color 0.15s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#57d7e2")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.92)")}>{author.name}</a>
                      <p style={{ margin: 0, fontFamily: ff, fontSize: "13px", color: "rgba(255,255,255,0.62)" }}>
                        {author.role} · {post.readingTime} min read
                      </p>
                    </div>
                  </div>
                )}
                {/* This article has a diagram — say so, and offer the jump.
                    They were injected mid-article with no signal anywhere. */}
                {hasDiagram(post.slug) && (
                  <a
                    href={`#${diagramAnchor(post.slug)}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(diagramAnchor(post.slug))?.scrollIntoView({ behavior: "smooth", block: "center" });
                    }}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: "7px",
                      marginTop: "18px", padding: "6px 12px",
                      borderRadius: "6px", border: "1px solid rgba(87,215,226,0.4)",
                      background: "rgba(87,215,226,0.08)", color: "#57d7e2",
                      fontFamily: ff, fontSize: "12px", fontWeight: 700,
                      letterSpacing: "0.06em", textTransform: "uppercase",
                      textDecoration: "none",
                    }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <rect x="3" y="12" width="4" height="9" rx="1" fill="currentColor" opacity="0.55" />
                      <rect x="10" y="7" width="4" height="14" rx="1" fill="currentColor" opacity="0.8" />
                      <rect x="17" y="3" width="4" height="18" rx="1" fill="currentColor" />
                    </svg>
                    Interactive diagram
                  </a>
                )}

                {/* Freshness. An updated date is a trust signal this blog
                    writes about constantly but never showed. */}
                <p style={{ margin: "16px 0 0", fontFamily: ff, fontSize: "13px", color: "rgba(255,255,255,0.62)", letterSpacing: "0.01em" }}>
                  Published <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                  {post.updatedAt && post.updatedAt !== post.publishedAt && (
                    <>
                      {" · "}
                      <span style={{ color: "#57d7e2", fontWeight: 600 }}>
                        Updated <time dateTime={post.updatedAt}>{formatDate(post.updatedAt)}</time>
                      </span>
                    </>
                  )}
                </p>
              </div>

              {/* RIGHT: cover image (only if available) */}
              {post.coverImage && !isMobile && (
                <div style={{ borderRadius: "12px", overflow: "hidden", lineHeight: 0, aspectRatio: "16/9" }}>
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── BODY: table of contents + one reading column ──
            The right rail is gone. It squeezed the reading column to 588px
            between a rail that sells and a rail that recommends; "read next"
            now waits at the end of the article, where a reader who has
            finished is actually looking for it. */}
        <div style={{
          // 1240px, the same container as the hero above, so the table of
          // contents starts on the headline's left edge instead of 70px
          // inside it.
          maxWidth: "1240px", margin: "0 auto",
          padding: isMobile ? "0 20px 60px" : "0 48px 80px",
          display: isMobile ? "block" : "grid",
          // 640px at 19px in a face averaging 0.478em per character is ~70
          // characters a line. Barlow is narrow, so the column is narrower
          // than the 720px a normal-width face would take for the same
          // measure — set to 720px it ran to 84 characters, well past the
          // point where the eye loses the start of the next line.
          gridTemplateColumns: "200px minmax(0, 640px)",
          // Left-aligned, not centered: centering the 896px of grid inside the
          // 1100px container pushed the table of contents 124px right of the
          // headline above it, so the page had two left edges.
          gap: "56px",
          alignItems: "start",
          overflow: "visible",
        }}>

          {/* LEFT: sticky TOC. The dark CTA card that sat under it is now a
              quiet text link — it used to be on screen alongside an inline CTA
              carrying the identical headline. */}
          <aside style={{ position: "sticky", top: "96px", paddingTop: "48px", alignSelf: "start", display: isMobile ? "none" : undefined }}>
            <TableOfContents headings={headings} activeId={activeId} />
            <a
              href={STRATEGIST_URL}
              style={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                marginTop: "28px", paddingTop: "18px",
                borderTop: "1px solid rgba(0,0,0,0.1)",
                color: "#157f8b", fontSize: "13px", fontWeight: 700, fontFamily: ff,
                textDecoration: "none", lineHeight: 1.4,
              }}
            >
              Free video review
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          </aside>

          {/* CENTER: Article, in the original type. */}
          <article style={{ color: "#333", fontFamily: reading, fontSize: isMobile ? "16px" : "17px", lineHeight: 1.75, paddingTop: "40px", minWidth: 0 }}>
            {/* Mobile table of contents. Both rails are hidden below 900px and
                nothing replaced them, which left phone readers of a 20,000px
                article with no way to jump between sections. */}
            {isMobile && headings.length > 2 && (
              <nav className="mobile-toc" aria-label="On this page">
                <button
                  type="button"
                  id="mobile-toc-button"
                  onClick={() => setTocOpen((o) => !o)}
                  aria-expanded={tocOpen}
                  aria-controls="mobile-toc-list"
                >
                  <span>On this page</span>
                  <span className="mobile-toc-count">{headings.length} sections</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                    style={{ transform: tocOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s", flexShrink: 0 }}>
                    <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                <ol id="mobile-toc-list" hidden={!tocOpen}>
                  {headings.map((h) => (
                    <li key={h.id}>
                      <a
                        href={`#${h.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          setTocOpen(false);
                          document.getElementById(h.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                        }}
                      >
                        {h.text}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            )}

            {/* Key takeaways — authored, or written by the pipeline, or
                derived; never a raw-text fallback. */}
            {keyTakeawayBox && <div dangerouslySetInnerHTML={{ __html: keyTakeawayBox }} />}
            {/* Body, with the CTA and diagram rendered as components in place */}
            <ArticleBody slug={post.slug} html={processedContent} cta={inlineCta} cta2={listCtas?.[1]} caseStudy={caseStudy} />

            {/* Author bio at bottom */}
            {author && (
              <div style={{ marginTop: "64px", paddingTop: "32px", borderTop: "2px solid rgba(0,0,0,0.08)", display: "flex", alignItems: "flex-start", gap: "20px" }}>
                <a href={`/author/${author.slug}`} style={{ textDecoration: "none", flexShrink: 0 }}>
                  <img loading="lazy" src={authorPhoto(author)} alt={author.name} style={{ width: "72px", height: "72px", borderRadius: "50%", objectFit: "cover" }} />
                </a>
                <div style={{ flex: 1 }}>
                  <p style={{ color: "#5f6b6d", fontSize: "11px", fontFamily: ff, margin: "0 0 4px", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Written by</p>
                  <a href={`/author/${author.slug}`} style={{ color: "#111", fontSize: "17px", fontWeight: 800, fontFamily: ff, textDecoration: "none" }}>{author.name}</a>
                  <p style={{ color: "#157f8b", fontSize: "13px", fontFamily: ff, margin: "2px 0 10px", fontWeight: 600 }}>
                    {author.role}, Indexed
                    {author.reviewedBy && ` · Reviewed by ${author.reviewedBy}`}
                  </p>
                  {/* The bio is 220 characters long, so slicing it to 220 and
                      appending an ellipsis truncated the last word for nothing. */}
                  <p style={{ color: "#42494b", fontSize: "15px", fontFamily: reading, lineHeight: 1.6, margin: "0 0 16px" }}>{author.bio}</p>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ color: "#5f6b6d", fontSize: "12px", fontWeight: 700, fontFamily: ff, letterSpacing: "0.08em", textTransform: "uppercase", marginRight: "4px" }}>Share</span>
                    <CopyLinkBtn url={pageUrl} />
                    <ShareBtn href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(post.title)}`} label="Share on X">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                    </ShareBtn>
                    <ShareBtn href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`} label="Share on LinkedIn">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    </ShareBtn>
                    <ShareBtn href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`} label="Share on Facebook">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    </ShareBtn>
                  </div>
                </div>
              </div>
            )}
            {/* No "How to cite this" here.

                It belongs on a page that has something of its own to cite: the
                studies render it themselves, and the statistics hubs carry
                their own cite section inside their body. Rendering it for every
                post put a citation box on 449 pages, most of which — a tools
                round-up, a pricing comparison — contain no original figure. A
                request to be credited for a listicle is one no writer will
                honor, and on the hubs it printed the box twice. */}

            {/* ── What changed, for a guide that is maintained ──
                A blog that sells freshness as a ranking and trust signal
                should show its own working. Absent on most posts. */}
            {post.changelog && post.changelog.length > 0 && (
              <section className="changelog" aria-labelledby="changelog-heading">
                <h2 id="changelog-heading">Updates to this guide</h2>
                <ol>
                  {post.changelog.map((entry) => (
                    <li key={`${entry.date}-${entry.note}`}>
                      <time dateTime={entry.date}>{formatDate(entry.date)}</time>
                      <span>{entry.note}</span>
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {/* ── Read next — an end-of-article shelf, not a rail ── */}
            {related.length > 0 && (
              <section style={{ marginTop: "56px", paddingTop: "36px", borderTop: "1px solid rgba(0,0,0,0.1)" }} aria-labelledby="read-next">
                <h2 id="read-next" style={{ color: "#111", fontSize: "13px", fontWeight: 800, fontFamily: ff, letterSpacing: "0.1em", textTransform: "uppercase", margin: "0 0 18px" }}>
                  What to read next
                </h2>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "14px" }}>
                  {related.slice(0, 4).map((p) => {
                    const cat = p.categories[0] ? getCategoryBySlug(p.categories[0]) : null;
                    return (
                      <a key={p.slug} href={`/blog/${p.slug}`} className="read-next-card">
                        {cat && <span className="read-next-cat" style={{ color: cat.color }}>{cat.label}</span>}
                        {/* Title-tag tails ("... | The Data") are written for a
                            SERP snippet and read as a fragment here. */}
                        <span className="read-next-title">{stripTitleSuffix(p.title)}</span>
                      </a>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ── The one closing module. The reader who finished the article
                gets asked for an email, not a sales call — the offer already
                appeared once, mid-article and matched to the topic. ── */}
            <NewsletterForm source={slug || "blog"} idPrefix="blog-subscribe" />
          </article>
        </div>

        {/* ── STYLES ── */}
        <style>{`
          /* Reading progress. Where the browser supports scroll-driven
             animations this is pure CSS and no JS runs for it at all. */
          @keyframes reading-progress-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
          @supports (animation-timeline: scroll()) {
            .reading-progress { animation: reading-progress-grow linear forwards; animation-timeline: scroll(root block); }
          }
          @media (prefers-reduced-motion: reduce) {
            .reading-progress { transition: none; }
          }

          /* Mobile table of contents — the rails are hidden below 900px. */
          .mobile-toc { margin:0 0 28px; border:1px solid rgba(0,0,0,0.12); border-radius:10px; background:#f8f9fa; overflow:hidden; }
          .mobile-toc > button {
            display:flex; align-items:center; gap:10px; width:100%; min-height:52px;
            padding:14px 16px; background:none; border:none; cursor:pointer;
            font-family:'Barlow',sans-serif; font-size:14px; font-weight:700; color:#15191a;
            letter-spacing:0.02em; text-align:left;
          }
          .mobile-toc > button > span:first-child { flex:1; }
          .mobile-toc .mobile-toc-count { font-weight:500; font-size:12px; color:#5f6b6d; }
          .mobile-toc ol { list-style:none; margin:0; padding:0 16px 12px; display:flex; flex-direction:column; }
          .mobile-toc li { margin:0; }
          .mobile-toc ol a {
            display:flex; align-items:center; min-height:44px; padding:6px 0;
            border-top:1px solid rgba(0,0,0,0.07);
            color:#3a4446; font-size:15px; font-family:'Barlow',sans-serif; font-weight:500;
            text-decoration:none; line-height:1.35;
          }
          .mobile-toc ol a:hover, .mobile-toc ol a:focus-visible { color:#157f8b; }

          /* ── Prose scale — the original scale, restored. ── */
          article h1 { color:#111; font-size:32px; font-weight:900; font-family:'Barlow',sans-serif; margin:48px 0 18px; line-height:1.12; letter-spacing:-0.025em; text-wrap:balance; }
          article h2 { color:#111; font-size:26px; font-weight:800; font-family:'Barlow',sans-serif; margin:52px 0 16px; line-height:1.18; letter-spacing:-0.02em; text-wrap:balance; }
          article h3 { color:#222; font-size:20px; font-weight:700; font-family:'Barlow',sans-serif; margin:36px 0 12px; line-height:1.25; }
          article h4 { color:#333; font-size:17px; font-weight:700; font-family:'Barlow',sans-serif; margin:28px 0 10px; line-height:1.3; }
          article p { margin:0 0 20px; color:#333; }
          /* #0fb8c9 measured 2.4:1 on white — below the 4.5:1 floor for body
             text. #157f8b is the same hue at 4.7:1. */
          article a { color:#157f8b; text-decoration:underline; text-underline-offset:3px; text-decoration-thickness:1px; font-weight:inherit; }
          article a:hover { color:#0d5a63; text-decoration-thickness:2px; }
          article ul { list-style:disc; padding-left:26px; margin:0 0 1em; }
          article ol { list-style:decimal; padding-left:26px; margin:0 0 1em; }
          article li { margin-bottom:8px; color:#333; }
          article li::marker { color:#8a9698; }
          article strong { color:#111; font-weight:700; }
          article em { font-style:italic; }
          article blockquote { border-left:4px solid #57d7e2; padding:12px 0 12px 24px; margin:32px 0; color:#555; font-style:italic; background:#f8fffe; border-radius:0 8px 8px 0; }
          article blockquote p:last-child { margin-bottom:0; }

          article .tip-box { background:rgba(87,215,226,0.06); border:1px solid rgba(87,215,226,0.3); border-radius:10px; padding:16px 20px; margin:28px 0; color:#333; font-size:15px; }
          article .formula { background:#f0fafb; border-left:4px solid #57d7e2; border-radius:0 8px 8px 0; padding:14px 20px; margin:24px 0; color:#111; font-weight:700; font-size:16px; }

          /* Code & prompt-template blocks — must wrap, never overflow the column */
          article pre { background:#f8f9fa; border:1px solid rgba(0,0,0,0.1); border-left:4px solid #57d7e2; border-radius:0 10px 10px 0; padding:18px 22px; margin:28px 0; max-width:100%; white-space:pre-wrap; word-break:break-word; overflow-wrap:anywhere; overflow-x:auto; }
          article pre code { display:block; background:transparent; border:none; padding:0; color:#333; font-size:14px; line-height:1.7; font-family:'SF Mono','Monaco','Cascadia Code','Roboto Mono',monospace; white-space:inherit; word-break:inherit; overflow-wrap:inherit; }
          article code { background:#f0f1f2; border-radius:4px; padding:2px 6px; color:#111; font-size:0.88em; overflow-wrap:anywhere; }

          /* Callout boxes */
          article .definition-box { background:#f0fafb; border:1px solid rgba(87,215,226,0.35); border-left:4px solid #57d7e2; border-radius:0 10px 10px 0; padding:18px 22px; margin:32px 0; }
          article .definition-box strong, article .definition-box b { color:#157f8b; }
          article .pro-tip { background:#f6fef9; border:1px solid rgba(34,197,94,0.3); border-left:4px solid #22c55e; border-radius:0 10px 10px 0; padding:18px 22px; margin:32px 0; }
          article .pro-tip::before { content:"Pro Tip"; display:block; font-size:11px; font-weight:800; letter-spacing:0.1em; text-transform:uppercase; color:#16a34a; margin-bottom:8px; font-family:'Barlow',sans-serif; }
          article .warning { background:#fffbf0; border:1px solid rgba(245,158,11,0.3); border-left:4px solid #f59e0b; border-radius:0 10px 10px 0; padding:18px 22px; margin:32px 0; }
          article .warning::before { content:"Watch Out"; display:block; font-size:11px; font-weight:800; letter-spacing:0.1em; text-transform:uppercase; color:#d97706; margin-bottom:8px; font-family:'Barlow',sans-serif; }
          article .insight { background:#faf5ff; border:1px solid rgba(139,92,246,0.25); border-left:4px solid #8b5cf6; border-radius:0 10px 10px 0; padding:18px 22px; margin:32px 0; }
          article .insight::before { content:"Key Insight"; display:block; font-size:11px; font-weight:800; letter-spacing:0.1em; text-transform:uppercase; color:#7c3aed; margin-bottom:8px; font-family:'Barlow',sans-serif; }
          /* The one number an article wants you to remember. Light, like every
             other in-column module, with the attribution promoted out of the
             claim into its own line — a figure this prominent without a
             visible source is the thing this blog tells readers to distrust. */
          article .stat-callout {
            background:#f7fbfb; border:1px solid rgba(21,127,139,0.22); border-left:3px solid #157f8b;
            border-radius:0 10px 10px 0; padding:22px 26px; margin:36px 0;
            font-family:'Barlow','Barlow Fallback',sans-serif;
          }
          article .stat-callout .stat-number { color:#0d5a63; font-size:46px; font-weight:800; font-family:'Barlow',sans-serif; line-height:1; display:block; margin-bottom:8px; letter-spacing:-0.02em; }
          article .stat-callout .stat-label { color:#22282a; font-size:15px; font-family:'Barlow',sans-serif; line-height:1.5; display:block; }
          article .stat-callout .stat-source {
            display:block; margin-top:10px; padding-top:9px;
            border-top:1px solid rgba(21,127,139,0.18);
            color:#5f6b6d; font-size:12px; font-weight:600;
            letter-spacing:0.04em; text-transform:uppercase;
            font-family:'Barlow',sans-serif;
          }
          article .stat-callout .stat-source::before { content:"Source: "; color:#8a9698; }
          /* One published callout has an inline white color on its source
             link, left over from when this box was dark. An inline style beats
             a stylesheet rule, so this one needs the override to be visible on
             a light surface. */
          article .stat-callout .stat-source a {
            color:#157f8b !important; text-transform:none; letter-spacing:0; font-weight:600;
          }
          article .stat-callout .stat-label a { color:#157f8b !important; }

          /* What We Covered */
          article .what-we-covered { background:#f8f9fa; border:1px solid rgba(0,0,0,0.08); border-left:4px solid #111; border-radius:0 12px 12px 0; padding:24px 28px; margin:52px 0 36px; }
          article .what-we-covered h4 { color:#111 !important; font-size:13px !important; font-weight:800 !important; font-family:'Barlow',sans-serif !important; margin:0 0 14px !important; text-transform:uppercase !important; letter-spacing:0.08em !important; }
          article .what-we-covered ul { list-style:none !important; padding:0 !important; margin:0 !important; display:flex; flex-direction:column; gap:6px; }
          article .what-we-covered li { display:flex !important; align-items:flex-start !important; gap:10px !important; color:#555 !important; font-size:14px !important; margin:0 !important; }
          article .what-we-covered li::before { content:"→"; color:#157f8b; font-weight:700; flex-shrink:0; }
          article img { width:100%; border-radius:12px; margin:28px 0; }

          /* Key Takeaway */
          article .key-takeaway { background:#f8f9fa; border:1px solid rgba(0,0,0,0.08); border-left:4px solid #57d7e2; border-radius:0 12px 12px 0; padding:24px 28px; margin:36px 0; }
          article .key-takeaway h3, article .key-takeaway h4 { color:#111 !important; font-size:13px !important; font-weight:800 !important; font-family:'Barlow',sans-serif !important; margin:0 0 14px !important; text-transform:uppercase !important; letter-spacing:0.08em !important; }
          article .key-takeaway ul { list-style:none !important; padding:0 !important; margin:0 !important; display:flex; flex-direction:column; gap:8px; }
          article .key-takeaway li { position:relative; padding-left:22px; color:#333 !important; font-size:15px !important; margin:0 !important; line-height:1.6; }
          article .key-takeaway li::before { content:"✓"; color:#57d7e2; font-weight:700; font-size:14px; position:absolute; left:0; top:2px; }

          /* Tables — wrapContentTables() puts every table inside .table-scroll.
             The wrapper owns the frame and the horizontal scroll, so a table
             with more columns than the center column can hold scrolls inside
             the article instead of painting over the right-hand rail. */
          article .table-scroll {
            max-width:100%; overflow-x:auto; overscroll-behavior-x:contain; -webkit-overflow-scrolling:touch;
            margin:32px 0; border:1px solid rgba(0,0,0,0.1); border-radius:10px;
            scrollbar-width:thin; scrollbar-color:rgba(0,0,0,0.28) rgba(0,0,0,0.04);
            /* Scroll affordance: a shadow on whichever edge still has content
               past it. The paired "local" white gradients scroll with the
               content, so each shadow fades out once that end is reached. */
            background:
              linear-gradient(to right, #fff, rgba(255,255,255,0)) 0 0 / 28px 100% no-repeat local,
              linear-gradient(to left, #fff, rgba(255,255,255,0)) 100% 0 / 28px 100% no-repeat local,
              radial-gradient(farthest-side at 0 50%, rgba(0,0,0,0.2), rgba(0,0,0,0)) 0 0 / 14px 100% no-repeat scroll,
              radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.2), rgba(0,0,0,0)) 100% 0 / 14px 100% no-repeat scroll;
          }
          article .table-scroll:focus-visible { outline:2px solid #57d7e2; outline-offset:2px; }
          /* Wide tables keep a readable width per column and scroll, rather than
             squeezing each cell down to one word per line. --cols comes from the
             wrapper; narrow tables stay under the column width and so fill it. */
          article .table-scroll table { margin:0; border:none; border-radius:0; min-width:calc(var(--cols, 0) * 124px); }
          article .table-scroll::-webkit-scrollbar { height:8px; }
          article .table-scroll::-webkit-scrollbar-track { background:rgba(0,0,0,0.04); }
          article .table-scroll::-webkit-scrollbar-thumb { background:rgba(0,0,0,0.24); border-radius:4px; }
          article .table-scroll[data-cols="6"] table,
          article .table-scroll[data-cols="7"] table,
          article .table-scroll[data-cols="8"] table { font-size:14px; }
          article table { width:100%; border-collapse:collapse; margin:32px 0; font-size:15px; border:1px solid rgba(0,0,0,0.1); border-radius:10px; overflow:hidden; }
          article thead { background:#f0f1f2; }
          article th { color:#111; font-weight:700; font-family:'Barlow',sans-serif; padding:12px 16px; text-align:left; border-bottom:2px solid rgba(0,0,0,0.1); font-size:14px; overflow-wrap:break-word; }
          article td { padding:11px 16px; border-bottom:1px solid rgba(0,0,0,0.06); color:#444; vertical-align:top; overflow-wrap:break-word; }
          article tr:last-child td { border-bottom:none; }
          article tbody tr:hover td { background:#fafafa; }
          /* Single-cell tables used as callout/quote boxes — render as plain text */
          article table.blog-table:has(thead tr:only-child th:only-child):not(:has(td)) {
            border:none; border-radius:0; margin:0 0 20px; background:transparent;
          }
          article .table-scroll:has(> table.blog-table:has(thead tr:only-child th:only-child):not(:has(td))) {
            border:none; border-radius:0; margin:0; overflow:visible; background:none;
          }
          article table.blog-table:has(thead tr:only-child th:only-child):not(:has(td)) thead {
            background:transparent;
          }
          article table.blog-table:has(thead tr:only-child th:only-child):not(:has(td)) th {
            padding:0; border:none; font-weight:400; font-size:17px; color:#333; line-height:1.75;
          }

          /* ── The one mid-article module ──
             It used to be a near-black panel dropped into a white column,
             twice per article. A light surface with a teal keyline reads as
             part of the document instead of an advert pasted over it. */
          article .inline-cta {
            margin:48px 0; padding:26px 28px;
            background:#f7fbfb;
            border:1px solid rgba(21,127,139,0.22);
            border-left:3px solid #157f8b;
            border-radius:0 10px 10px 0;
          }
          article .inline-cta-label {
            color:#157f8b !important; font-size:11px !important; font-weight:800 !important;
            letter-spacing:0.12em !important; text-transform:uppercase !important;
            margin:0 0 8px !important; font-family:'Barlow',sans-serif !important;
          }
          article .inline-cta-heading {
            color:#111 !important; font-size:21px !important; font-weight:800 !important;
            font-family:'Barlow',sans-serif !important; margin:0 0 8px !important;
            line-height:1.2 !important; letter-spacing:-0.02em !important;
          }
          article .inline-cta-body {
            color:#42494b !important; font-size:15px !important;
            margin:0 0 18px !important; line-height:1.55 !important;
            font-family:'Barlow',sans-serif !important; max-width:56ch;
          }
          article .inline-cta-btn {
            display:inline-flex; align-items:center; gap:8px;
            padding:11px 20px; background:#157f8b; border-radius:8px;
            color:#fff !important; font-size:14px; font-weight:700;
            font-family:'Barlow',sans-serif; text-decoration:none !important;
            transition:background 0.15s;
          }
          article .inline-cta-btn {
            border:none; cursor:pointer; line-height:1;
          }
          article .inline-cta-btn:hover { background:#0d5a63; color:#fff !important; }
          /* Tool-backed modules carry the tool's own input, so the reader can
             check their domain from the column instead of being sent to a
             landing page to start over. */
          article .inline-cta-form { display:flex; gap:10px; flex-wrap:wrap; max-width:440px; }
          article .inline-cta-form input {
            flex:1; min-width:190px; padding:10px 14px;
            background:#fff; border:1px solid rgba(0,0,0,0.2); border-radius:8px;
            color:#15191a; font-size:15px; font-family:'Barlow',sans-serif; outline:none;
          }
          article .inline-cta-form input::placeholder { color:#7b8688; }
          article .inline-cta-form input:focus-visible { border-color:#157f8b; box-shadow:0 0 0 3px rgba(21,127,139,0.15); }

          /* ── Client result, before the FAQ ──
             Same light surface as the mid-article module, but no button: it
             is evidence for the argument, not a second offer. */
          article .case-proof {
            margin:44px 0; padding:22px 26px;
            background:#fff;
            border:1px solid rgba(0,0,0,0.1);
            border-top:3px solid #157f8b;
            border-radius:0 0 10px 10px;
          }
          article .case-proof-label {
            color:#157f8b !important; font-size:11px !important; font-weight:800 !important;
            letter-spacing:0.12em !important; text-transform:uppercase !important;
            margin:0 0 8px !important; font-family:'Barlow',sans-serif !important;
          }
          article .case-proof-heading {
            color:#111 !important; font-size:18px !important; font-weight:700 !important;
            font-family:'Barlow',sans-serif !important; margin:0 0 16px !important;
            line-height:1.3 !important; letter-spacing:-0.01em !important;
          }
          article .case-proof-metrics { display:flex; flex-wrap:wrap; gap:12px 32px; margin:0 0 16px; }
          article .case-proof-metric { display:flex; flex-direction:column; gap:2px; min-width:120px; }
          article .case-proof-value {
            color:#157f8b; font-size:26px; font-weight:800; line-height:1.1;
            font-family:'Barlow',sans-serif; letter-spacing:-0.02em;
          }
          article .case-proof-metric-label { color:#5a6365; font-size:13px; font-family:'Barlow',sans-serif; }
          article .case-proof-link {
            color:#157f8b !important; font-size:14px; font-weight:700;
            font-family:'Barlow',sans-serif; text-decoration:none !important;
          }
          article .case-proof-link:hover { text-decoration:underline !important; }

          /* ── Read next, as an end-of-article shelf ── */
          article .read-next-card {
            display:flex; flex-direction:column; gap:5px;
            padding:16px 18px; border:1px solid rgba(0,0,0,0.1); border-radius:10px;
            background:#fff; text-decoration:none !important;
            transition:border-color 0.15s, background 0.15s;
          }
          article .read-next-card:hover { border-color:rgba(21,127,139,0.45); background:#f7fbfb; }
          article .read-next-cat {
            font-size:10px; font-weight:800; letter-spacing:0.09em; text-transform:uppercase;
          }
          article .read-next-title {
            color:#15191a; font-size:15px; font-weight:600; line-height:1.35; letter-spacing:-0.01em;
          }

          /* ── The one closing module ──
             Light, like the mid-article module: the whole point of the pass
             was that the reading column stops being studded with black
             rectangles. It is also directly above a dark footer, so a dark
             panel here would merge into it. */

          /* ── Changelog ── */
          article .changelog {
            margin:48px 0 0; padding:22px 0 0;
            border-top:1px solid rgba(0,0,0,0.1);
            font-family:'Barlow','Barlow Fallback',sans-serif;
          }
          article .changelog h2 {
            font-size:13px !important; font-weight:800 !important;
            letter-spacing:0.1em !important; text-transform:uppercase !important;
            color:#111 !important; margin:0 0 14px !important;
          }
          article .changelog ol { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:10px; }
          article .changelog li {
            display:grid; grid-template-columns:130px minmax(0,1fr); gap:14px;
            margin:0; color:#42494b; font-size:14.5px; line-height:1.5;
          }
          article .changelog time { color:#5f6b6d; font-weight:700; font-size:12px; letter-spacing:0.04em; text-transform:uppercase; padding-top:2px; }
          @media (max-width: 600px) {
            article .changelog li { grid-template-columns:1fr; gap:2px; }
          }

          .sr-only {
            position:absolute; width:1px; height:1px; padding:0; margin:-1px;
            overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0;
          }

          @media (max-width: 700px) {
            article .table-scroll { margin:24px 0; }
            article th, article td { padding:10px 12px; }
            article .inline-cta { padding:22px 20px; }
            article .inline-cta-form button { width:100%; justify-content:center; }
          }

          /* ── Narrow tables stack on a phone ──
             Horizontal scroll is right for a six-column comparison. On a
             three-column table it made every cell wrap to two words per line,
             so five rows took a screen and a half. Below 600px a stackable
             table becomes one labeled block per row; wider tables keep the
             scroll container. */
          @media (max-width: 600px) {
            article .table-scroll[data-stackable="true"] {
              overflow-x:visible; border:none; background:none;
            }
            article .table-scroll[data-stackable="true"] table {
              min-width:0; border:none; display:block;
            }
            article .table-scroll[data-stackable="true"] thead {
              position:absolute; width:1px; height:1px; overflow:hidden;
              clip:rect(0,0,0,0); white-space:nowrap;
            }
            article .table-scroll[data-stackable="true"] tbody { display:block; }
            article .table-scroll[data-stackable="true"] tr {
              display:block; margin:0 0 12px; padding:4px 16px 12px;
              border:1px solid rgba(0,0,0,0.12); border-radius:10px; background:#fff;
            }
            article .table-scroll[data-stackable="true"] td {
              display:grid; grid-template-columns:1fr; gap:2px;
              padding:10px 0; border-bottom:1px solid rgba(0,0,0,0.07);
              overflow-wrap:break-word;
            }
            article .table-scroll[data-stackable="true"] tr td:last-child { border-bottom:none; }
            article .table-scroll[data-stackable="true"] td[data-label]::before {
              content:attr(data-label);
              font-size:10px; font-weight:800; letter-spacing:0.09em;
              text-transform:uppercase; color:#5f6b6d;
              font-family:'Barlow',sans-serif;
            }
            article .table-scroll[data-stackable="true"] tbody tr:hover td { background:transparent; }
          }
          /* Auto key takeaway */
          article .auto-takeaway { margin-top:0 !important; margin-bottom:40px; }
        `}</style>
      </div>
    </SiteLayout>
  );
}
